import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { COUNTRIES, DEFAULT_COUNTRY, validatePassword } from "@/lib/countries";
import { landingFor } from "@/lib/landing";
import { useBranding } from "@/contexts/BrandingContext";

const SSO_ERROR_MESSAGES = {
  cancelled: "Microsoft sign-in was cancelled.",
  not_registered: "Your Microsoft account isn't registered in this workspace. Ask an administrator to sync your account from Microsoft first.",
  pending_approval: "Your account is still pending approval by your Business Unit owner.",
  account_denied: "Your account has been disabled. Please contact your administrator.",
  wrong_tenant: "Please sign in with your organisation's Microsoft account.",
  invalid_state: "The sign-in session expired. Please try again.",
  invalid_callback: "The Microsoft sign-in response was invalid.",
  identity_conflict: "This Microsoft account is linked to a different identity.",
  provider_error: "Microsoft sign-in is temporarily unavailable. Please try again shortly.",
  provider_unavailable: "Microsoft sign-in is temporarily unavailable. Please try again shortly.",
  sso_not_configured: "Microsoft SSO hasn't been configured on the server yet.",
  no_identity: "Microsoft didn't return enough information to sign you in.",
};


export default function Login() {
  const { user, login, error } = useAuth();
  const { app_name, app_logo } = useBranding();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [ssoConfigured, setSsoConfigured] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  // Surface SSO errors returned by /api/auth/microsoft/callback?error=...
  useEffect(() => {
    const err = searchParams.get("error");
    if (err) {
      toast.error(SSO_ERROR_MESSAGES[err] || "Microsoft sign-in failed.");
      const next = new URLSearchParams(searchParams);
      next.delete("error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Only show the SSO button when the backend has Azure creds configured.
  useEffect(() => {
    api.get("/auth/microsoft/status")
      .then(({ data }) => setSsoConfigured(Boolean(data?.configured)))
      .catch(() => setSsoConfigured(false));
  }, []);

  if (user) return <Navigate to={landingFor(user)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    // login() updates AuthContext synchronously via setUser — but we don't have
    // a fresh user object here. Best route: fetch the latest via /auth/me.
    if (ok) {
      try {
        const { data } = await api.get("/auth/me");
        nav(landingFor(data), { replace: true });
      } catch {
        nav("/business-units", { replace: true });
      }
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-5 bg-zinc-50"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
    >
      {/* Left visual panel */}
      <div className="hidden lg:flex lg:col-span-2 relative overflow-hidden bg-zinc-900 text-white p-12 flex-col justify-between">
        <div>
          {app_logo ? (
            <img src={app_logo} alt={app_name} className="max-h-14 max-w-[240px] object-contain" data-testid="login-app-logo" />
          ) : (
            <div
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="login-app-name"
            >
              {app_name}
            </div>
          )}
        </div>

        <div className="relative z-10">
          <h1
            className="text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Run a fair,
            <br /> structured
            <br /> performance year.
          </h1>
          <p className="mt-6 text-zinc-400 max-w-md text-sm leading-relaxed">
            Build Key Performance Area templates per Position Profile, lock them for the
            fiscal year, and run manager-employee reviews end-to-end with digital
            signatures and audit-ready records.
          </p>
        </div>

        <div className="text-xs text-zinc-500 grid grid-cols-3 gap-4 relative z-10">
          <div>
            <div className="text-zinc-300 text-sm font-semibold">3</div>
            <div>Review stages</div>
          </div>
          <div>
            <div className="text-zinc-300 text-sm font-semibold">FY-Locked</div>
            <div>Templates</div>
          </div>
          <div>
            <div className="text-zinc-300 text-sm font-semibold">Microsoft</div>
            <div>SSO ready</div>
          </div>
        </div>

        {/* decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      {/* Right form */}
      <div className="lg:col-span-3 flex items-center justify-center p-8">
        {mode === "signin" ? (
        <form onSubmit={onSubmit} className="w-full max-w-md">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sign in</div>
            <button type="button" onClick={() => setMode("register")} data-testid="show-register-btn" className="text-xs text-blue-600 hover:underline">Create account →</button>
          </div>
          <h2
            className="text-3xl font-bold tracking-tight text-zinc-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Welcome back.
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Use your work email or your organisation&apos;s Microsoft account.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.emailInput}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.passwordInput}
                className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
              />
            </div>

            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid={AUTH.submitButton}
              style={{ background: "var(--brand-primary)" }}
              className="w-full text-white py-2.5 rounded-sm text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-right">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                data-testid="login-forgot-link"
                className="text-xs text-zinc-600 hover:text-zinc-900 underline"
              >
                Forgot password?
              </button>
            </div>

            <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-zinc-400">
              <div className="flex-1 h-px bg-zinc-200" /> or
              <div className="flex-1 h-px bg-zinc-200" />
            </div>

            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/auth/microsoft/start`}
              data-testid={AUTH.ssoButton}
              aria-disabled={!ssoConfigured}
              onClick={(e) => {
                if (!ssoConfigured) {
                  e.preventDefault();
                  toast.error(SSO_ERROR_MESSAGES.sso_not_configured);
                }
              }}
              className={`w-full flex items-center justify-center gap-2 border border-zinc-300 bg-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-50 ${!ssoConfigured ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <svg viewBox="0 0 23 23" className="w-4 h-4">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Sign in with Microsoft
            </a>
          </div>
        </form>
        ) : (
          <RegisterForm onCancel={() => setMode("signin")} onDone={() => setMode("signin")} />
        )}
      </div>
      {forgotOpen && <ForgotPasswordModal defaultEmail={email} onClose={() => setForgotOpen(false)} />}
    </div>
  );
}

function RegisterForm({ onCancel, onDone }) {
  const [bus, setBus] = useState([]);
  const [form, setForm] = useState({
    name: "", surname: "", email: "",
    business_unit_id: "",
    phone_country: DEFAULT_COUNTRY, phone_number: "",
    password: "", confirm: "",
    website: "", // honeypot — must stay empty
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/auth/register-options").then(({ data }) => {
      setBus(data.business_units || []);
      if (data.business_units?.length) setForm((f) => ({ ...f, business_unit_id: data.business_units[0].id }));
    }).catch(() => {});
  }, []);

  const country = COUNTRIES.find((c) => c.iso === form.phone_country) || COUNTRIES[0];
  const phoneFull = `${country.dial} ${form.phone_number.trim()}`;
  const pwErr = form.password ? validatePassword(form.password) : null;
  const mismatch = form.password && form.confirm && form.password !== form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (pwErr) return toast.error(`Password: ${pwErr}`);
    if (mismatch) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/register", {
        name: form.name, surname: form.surname, email: form.email,
        business_unit_id: form.business_unit_id,
        phone_country: form.phone_country, phone_number: phoneFull,
        password: form.password,
        website: form.website,
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Registration failed";
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="w-full max-w-md">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Registration submitted</div>
        <h2 className="text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Pending approval</h2>
        <p className="text-sm text-zinc-600 mt-3">
          Thanks {form.name}. Your registration for your Business Unit is awaiting approval from its owner. You&apos;ll receive a notification when you can sign in.
        </p>
        <button onClick={onDone} data-testid="register-back-signin-btn" className="mt-6 w-full bg-zinc-900 text-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800">Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-md">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Create account</div>
        <button type="button" onClick={onCancel} data-testid="show-signin-btn" className="text-xs text-blue-600 hover:underline">← Back to sign in</button>
      </div>
      <h2 className="text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Register</h2>
      <p className="text-sm text-zinc-500 mt-1">Your Business Unit owner will approve your account before first sign-in.</p>

      {/* Honeypot — hidden from real users but visible to dumb bots. Must remain empty. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="hp-website">Website</label>
        <input
          id="hp-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
        />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <RField label="First name"><input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="reg-name-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" /></RField>
        <RField label="Surname"><input required value={form.surname} onChange={(e) => setForm({...form, surname: e.target.value})} data-testid="reg-surname-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" /></RField>
        <div className="col-span-2"><RField label="Email"><input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="reg-email-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" placeholder="you@company.com" /></RField></div>
        <div className="col-span-2">
          <RField label="Business unit">
            <select required value={form.business_unit_id} onChange={(e) => setForm({...form, business_unit_id: e.target.value})} data-testid="reg-bu-select" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white">
              {bus.length === 0 && <option value="">No business units configured</option>}
              {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </RField>
        </div>
        <div className="col-span-2">
          <RField label="Phone number">
            <div className="flex gap-2">
              <select value={form.phone_country} onChange={(e) => setForm({...form, phone_country: e.target.value})} data-testid="reg-phone-country-select"
                className="border border-zinc-300 rounded-sm px-2 py-2 text-sm bg-white">
                {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.flag} {c.dial}</option>)}
              </select>
              <input value={form.phone_number} onChange={(e) => setForm({...form, phone_number: e.target.value})} data-testid="reg-phone-input" placeholder="121231234" className="flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono" />
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">Will be stored as <code className="bg-zinc-100 px-1">{phoneFull || "+27 121231234"}</code></div>
          </RField>
        </div>
        <div className="col-span-2"><RField label="Password"><input type="password" required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="reg-password-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" /></RField></div>
        <div className="col-span-2"><RField label="Confirm password"><input type="password" required value={form.confirm} onChange={(e) => setForm({...form, confirm: e.target.value})} data-testid="reg-confirm-input" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" /></RField></div>
      </div>

      <ul className="mt-3 text-[11px] text-zinc-600 grid grid-cols-2 gap-x-3 gap-y-1">
        <Rule ok={form.password.length >= 8}>At least 8 characters</Rule>
        <Rule ok={/[A-Z]/.test(form.password)}>Uppercase letter</Rule>
        <Rule ok={/[a-z]/.test(form.password)}>Lowercase letter</Rule>
        <Rule ok={/\d/.test(form.password)}>Number</Rule>
        <Rule ok={/[^A-Za-z0-9]/.test(form.password)}>Special character</Rule>
        <Rule ok={form.password && form.password === form.confirm}>Passwords match</Rule>
      </ul>

      <button type="submit" disabled={busy} data-testid="reg-submit-btn" className="mt-5 w-full bg-zinc-900 text-white py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-60">
        {busy ? "Submitting…" : "Create account"}
      </button>
    </form>
  );
}

function RField({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Rule({ ok, children }) {
  return <li className={ok ? "text-emerald-700" : "text-zinc-500"}>{ok ? "\u2713" : "\u2022"} {children}</li>;
}

function ForgotPasswordModal({ defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      setSent(true); // Don't leak whether email exists
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">Forgot password</div>
        <div className="p-5">
          {sent ? (
            <div className="space-y-4 text-sm text-zinc-700">
              <p>
                If <b>{email}</b> is registered, an administrator has been notified and will issue a new temporary password shortly. You&apos;ll receive a welcome email and be asked to set a new password on next sign-in.
              </p>
              <div className="flex justify-end">
                <button onClick={onClose} data-testid="forgot-close-btn" className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Got it</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-zinc-600">Enter your work email. A super-administrator will approve the request before a new temporary password is issued.</p>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Email</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="w-full mt-1 border border-zinc-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
                <button type="submit" disabled={busy} data-testid="forgot-submit-btn" className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-60">{busy ? "Requesting…" : "Request reset"}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
